/**
 * A franquia de corretivas do mês (C1/D2, 06/10/2026) — normalização da resposta e os textos que a aba
 * Contratos dos Indicadores e a ficha do cliente compartilham. Ajudante puro mora em `.ts` (régua da casa).
 *
 * 🔴 Os textos daqui vão para a tela: sem travessão, sem "(s)", e os códigos do servidor
 * (`a_calcular`, `sem_regra`) viram frase de gente.
 */
import { hojeBR } from '@/lib/datas-brasilia';
import { formatarData, mesPorExtenso } from '@/lib/format-date';
import { horaDoAuvo } from '@/lib/manutencao/hora-do-auvo';

import type {
  ConflitoDaFranquia,
  CorretivaExcedente,
  FranquiaDoContrato,
  FranquiaDoMes,
  ModoDaFranquia,
  UsoDoSistema,
} from './franquia-tipos';

// ─── Normalização (resposta torta não derruba a tela) ──────────────────────

const lista = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);
const num = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0);
const numOuNulo = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
const txt = (x: unknown): string => (typeof x === 'string' ? x : '');
const txtOuNulo = (x: unknown): string | null => (typeof x === 'string' && x ? x : null);
const obj = (x: unknown): Record<string, unknown> =>
  x && typeof x === 'object' ? (x as Record<string, unknown>) : {};

const MODOS: ModoDaFranquia[] = ['numerico', 'ilimitado', 'nenhum'];

function uso(x: unknown): UsoDoSistema {
  const o = obj(x);
  const modo = MODOS.includes(o.modo as ModoDaFranquia) ? (o.modo as ModoDaFranquia) : 'numerico';
  return {
    escopoId: txt(o.escopoId),
    sistemaId: txt(o.sistemaId),
    sistemaNome: txt(o.sistemaNome) || 'Sistema sem nome',
    usadas: num(o.usadas),
    inclusas: modo === 'ilimitado' ? null : numOuNulo(o.inclusas) ?? 0,
    modo,
    excedentes: num(o.excedentes),
    valorExcedenteCentavos: num(o.valorExcedenteCentavos),
    excedentesSemValor: num(o.excedentesSemValor),
    regraTexto: txt(o.regraTexto),
  };
}

function contrato(x: unknown): FranquiaDoContrato | null {
  const o = obj(x);
  if (typeof o.contratoId !== 'string' || !Array.isArray(o.sistemas)) return null;
  return {
    contratoId: o.contratoId,
    contratoTitulo: txt(o.contratoTitulo),
    clienteId: txt(o.clienteId),
    cliente: txt(o.cliente) || 'Cliente sem nome',
    sistemas: o.sistemas.map(uso),
    excedentes: num(o.excedentes),
    valorExcedenteCentavos: num(o.valorExcedenteCentavos),
  };
}

function excedente(x: unknown): CorretivaExcedente | null {
  const o = obj(x);
  if (typeof o.taskId !== 'string') return null;
  const semValor = o.semValor === 'a_calcular' || o.semValor === 'sem_regra' ? o.semValor : null;
  return {
    taskId: o.taskId,
    data: txt(o.data),
    entradaIso: txtOuNulo(o.entradaIso),
    tecnico: txt(o.tecnico) || 'Técnico sem nome',
    clienteId: txt(o.clienteId),
    clienteNome: txt(o.clienteNome),
    contratoId: txt(o.contratoId),
    escopoId: txt(o.escopoId),
    sistemaId: txt(o.sistemaId),
    sistemaNome: txt(o.sistemaNome),
    minutosNoLocal: numOuNulo(o.minutosNoLocal),
    horasCobradas: numOuNulo(o.horasCobradas),
    valorCentavos: numOuNulo(o.valorCentavos),
    regraTexto: txt(o.regraTexto),
    urlNoAuvo: txtOuNulo(o.urlNoAuvo),
    semValor,
  };
}

function conflito(x: unknown): ConflitoDaFranquia | null {
  const o = obj(x);
  if (typeof o.taskId !== 'string') return null;
  return {
    taskId: o.taskId,
    data: txt(o.data),
    clienteId: txt(o.clienteId),
    cliente: txt(o.cliente) || 'Cliente sem nome',
    sistemaNome: txt(o.sistemaNome),
    contratos: lista(o.contratos).filter((c): c is string => typeof c === 'string'),
  };
}

function naoNulo<T>(x: T | null): x is T {
  return x !== null;
}

/** A resposta do servidor, defensiva: campo faltando vira vazio, nunca exceção. */
export function normalizarFranquia(x: unknown): FranquiaDoMes {
  const o = obj(x);
  const t = obj(o.totais);
  const mes = /^\d{4}-(0[1-9]|1[0-2])$/.test(txt(o.mes)) ? txt(o.mes) : mesAtualBR();
  return {
    mes,
    contratos: lista(o.contratos).map(contrato).filter(naoNulo),
    excedentes: lista(o.excedentes).map(excedente).filter(naoNulo),
    conflitos: lista(o.conflitos).map(conflito).filter(naoNulo),
    semSistema: num(o.semSistema),
    semContratoDoSistema: num(o.semContratoDoSistema),
    // ⚠️ #420: servidor antigo não manda a lista — vira vazia, nunca `undefined.map`.
    semCliente: lista(o.semCliente)
      .map((s) => {
        const c = obj(s);
        const taskId = txt(c.taskId);
        if (!taskId) return null;
        return {
          taskId,
          data: txt(c.data),
          tecnico: txt(c.tecnico),
          nomeNoAuvo: txt(c.nomeNoAuvo) || '(sem nome no Auvo)',
          codigoClienteAuvo: txt(c.codigoClienteAuvo) || null,
          urlNoAuvo: txt(c.urlNoAuvo) || null,
        };
      })
      .filter(naoNulo),
    totais: {
      corretivas: num(t.corretivas),
      inclusas: num(t.inclusas),
      excedentes: num(t.excedentes),
      valorExcedenteCentavos: num(t.valorExcedenteCentavos),
    },
  };
}

// ─── Mês ─────────────────────────────────────────────────────────────────

/** O mês corrente de Brasília, `YYYY-MM`. */
export function mesAtualBR(): string {
  return hojeBR().slice(0, 7);
}

/** `?mesFranquia=` aceito só como `YYYY-MM`; o resto é ignorado (o servidor decide). */
export function mesValido(m: string | null | undefined): string | null {
  return m && /^\d{4}-(0[1-9]|1[0-2])$/.test(m) ? m : null;
}

/** Os últimos `n` meses terminando em `mesFinal` (o mais recente primeiro). */
export function ultimosMeses(mesFinal: string, n = 6): string[] {
  const [a, m] = mesFinal.split('-').map(Number);
  const r: string[] = [];
  for (let i = 0; i < n; i++) {
    const total = a * 12 + (m - 1) - i;
    r.push(`${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`);
  }
  return r;
}

/** "setembro de 2026". */
export function mesComAno(mes: string): string {
  return mesPorExtenso(mes) ?? mes;
}

/** "setembro" (sem o ano), para a linha da ficha. */
export function nomeDoMes(mes: string): string {
  return (mesPorExtenso(mes) ?? mes).replace(/ de \d{4}$/, '');
}

// ─── Textos ───────────────────────────────────────────────────────────────

/** "6 de 3", "3 de ilimitadas", "2 sem franquia". */
export function usadasTexto(u: Pick<UsoDoSistema, 'usadas' | 'inclusas' | 'modo'>): string {
  const usadas = u.usadas.toLocaleString('pt-BR');
  if (u.modo === 'ilimitado' || u.inclusas === null) return `${usadas} de ilimitadas`;
  if (u.modo === 'nenhum') return `${usadas} sem franquia`;
  return `${usadas} de ${u.inclusas.toLocaleString('pt-BR')}`;
}

/** "1 excedente", "3 excedentes". */
export function excedentesTexto(n: number): string {
  return `${n.toLocaleString('pt-BR')} ${n === 1 ? 'excedente' : 'excedentes'}`;
}

/** Centavos como R$. */
export function reaisDaFranquia(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** O valor de uma corretiva excedente, ou por que ela ainda não tem valor. */
export function valorDoExcedenteTexto(e: Pick<CorretivaExcedente, 'valorCentavos' | 'semValor'>): string {
  if (e.valorCentavos !== null) return reaisDaFranquia(e.valorCentavos);
  return e.semValor === 'a_calcular' ? 'a calcular' : 'sem regra de cobrança';
}

/** A regra do excedente como o servidor escreve, sem o travessão que ele usa para valor vazio. */
export function regraTexto(t: string): string {
  return (t || 'Sem regra de cobrança').replace(/\s*—\s*/g, ' sem valor ').replace(/\s+/g, ' ').trim();
}

/**
 * Dia e hora da corretiva, "14/09 08:30". O horário do Auvo vem sem fuso e é hora de parede de Brasília:
 * os dígitos são lidos como estão. Com fuso explícito, passa pelo formatador único. Sem entrada, só o dia.
 */
export function diaEHoraDaCorretiva(e: Pick<CorretivaExcedente, 'entradaIso' | 'data'>): string {
  const s = (e.entradaIso ?? '').trim();
  const comFuso = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  if (s && comFuso) {
    const dia = formatarData(s, { day: '2-digit', month: '2-digit' });
    const hora = formatarData(s, { hour: '2-digit', minute: '2-digit' });
    if (dia !== '—' && hora !== '—') return `${dia} ${hora}`;
  }
  // V1: a hora de parede do Auvo sai do formatador único (`horaDoAuvo`), o mesmo da aba Plantão, do PDF e da planilha.
  const entrada = horaDoAuvo(s);
  if (entrada.hora !== '—') return entrada.curta;
  const dia = horaDoAuvo(e.data).diaEMes;
  return dia !== '—' ? dia : 'sem data';
}

/** "1 h 05 min", "45 min", ou "sem duração". */
export function tempoNoLocalTexto(min: number | null): string {
  if (min === null) return 'sem duração';
  const total = Math.max(0, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h === 0 ? `${m} min` : `${h} h ${String(m).padStart(2, '0')} min`;
}

/** "1 h cobrada", "3 h cobradas". */
export function horasCobradasTexto(h: number | null): string | null {
  if (h === null) return null;
  return `${h.toLocaleString('pt-BR')} h ${h === 1 ? 'cobrada' : 'cobradas'}`;
}

/** O aviso dos conflitos, com o plural certo. */
export function conflitosTexto(n: number): string {
  return n === 1
    ? '1 corretiva de cliente com dois contratos ativos cobrindo o mesmo sistema. Confira os contratos.'
    : `${n.toLocaleString('pt-BR')} corretivas de clientes com dois contratos ativos cobrindo o mesmo sistema. Confira os contratos.`;
}

/** A linha das corretivas que não entram em franquia nenhuma por falta de contrato do sistema. */
export function semContratoTexto(n: number): string {
  return n === 1
    ? '1 corretiva de cliente sem contrato que cubra o sistema não entra na franquia.'
    : `${n.toLocaleString('pt-BR')} corretivas de clientes sem contrato que cubra o sistema não entram na franquia.`;
}

/** A linha das corretivas cujo tipo no Auvo não diz o sistema. */
export function semSistemaTexto(n: number): string {
  return n === 1
    ? '1 corretiva sem sistema identificado no tipo da tarefa não entra na franquia.'
    : `${n.toLocaleString('pt-BR')} corretivas sem sistema identificado no tipo da tarefa não entram na franquia.`;
}

/**
 * As corretivas excedentes de UM bloco do contrato.
 * ⚠️ A resposta não traz o id do bloco: o excedente diz o contrato e UM sistema, e o bloco diz todos os
 * sistemas que cobre ("CFTV + Controle de acesso"). Casa-se pelo contrato e pelo nome do sistema dentro
 * do bloco.
 */
export function excedentesDoBloco(
  todos: CorretivaExcedente[],
  contratoId: string,
  u: Pick<UsoDoSistema, 'escopoId' | 'sistemaId' | 'sistemaNome'>,
): CorretivaExcedente[] {
  // O bloco do contrato liga a linha ao uso (o servidor manda `escopoId` nos dois). Resposta antiga sem ele: cai no
  // casamento por contrato e nome do sistema.
  if (u.escopoId) return todos.filter((e) => e.escopoId === u.escopoId);
  const nomes = new Set(u.sistemaNome.split(' + ').map((s) => s.trim()));
  return todos.filter(
    (e) => e.contratoId === contratoId && (e.sistemaId === u.sistemaId || nomes.has(e.sistemaNome)),
  );
}
